import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import "./bookCartPage.css";
import BaseNavbar from "../../commanComponents/navbar/baseNavbar";
import Footer from "../../commanComponents/footer/footer";
import SkeletonLoading from "../../commanComponents/skeletonLoading/skeletonLoading";
import { bookCartListService, bookRemoveCartService } from "../../../modules/services";
import { ROUTES } from "../../../constant";


const BookCartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingItems, setProcessingItems] = useState(new Set());
  const [notification, setNotification] = useState({
    message: "",
    visible: false,
  });
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const navigate = useNavigate();

  // Simulate API fetch
  useEffect(() => {
    (async () => {
      setLoading(true);
      const responseData = await bookCartListService({});

      if (responseData === "error" || !responseData.data) {
        setLoading(false);
        return;
      }

      if (
        responseData.status_code !== 200 &&
        responseData.status_code !== 201
      ) {
        setLoading(false);
        return;
      }
      setCartItems(responseData.data.items);
      setLoading(false);
    })();
  }, []);

  // Fade-in animation on scroll
  useEffect(() => {
    // Force fade-in on load
    setTimeout(() => {
      document.querySelectorAll(".cart-fade-in").forEach((el) => {
        el.classList.add("visible");
      });
    }, 100); // 100ms delay = subtle animation

    // Optional: Still listen to scroll for future elements
    const handleScroll = () => {
      document.querySelectorAll(".cart-fade-in:not(.visible)").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          el.classList.add("visible");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const showNotification = (message) => {
    setNotification({ message, visible: true });
    setTimeout(() => setNotification({ message: "", visible: false }), 3000);
  };

const removeItem = (bookId, title) => {
  setProcessingItems((prev) => new Set(prev).add(bookId));

  (async () => {
    const responseData = await bookRemoveCartService(bookId, {}); // assuming this takes bookId or cartItemId?

    if (responseData === "error" || (responseData.status_code !== 200 && responseData.status_code !== 201)) {
      showNotification("Failed to remove item");
      setProcessingItems((prev) => {
        const next = new Set(prev);
        next.delete(bookId);
        return next;
      });
      return;
    }

    // Correctly filter by book.id
    setCartItems((prevItems) => 
      prevItems.filter((item) => item.book.id !== bookId)
    );
    window.dispatchEvent(new Event('cartUpdated'));

    setProcessingItems((prev) => {
      const next = new Set(prev);
      next.delete(bookId);
      return next;
    });

    showNotification(`${title} removed from cart`);
  })();
};

  const applyPromoCode = () => {
    // if (!promoCode.trim()) {
    //   showNotification("Please enter a promo code");
    //   return;
    // }
    // // Simulate promo code validation
    // const validCodes = {
    //   "SAVE10": { discount: 10, type: "percentage" },
    //   "FLAT100": { discount: 100, type: "flat" },
    //   "FIRSTBUY": { discount: 15, type: "percentage" }
    // };
    // if (validCodes[promoCode.toUpperCase()]) {
    //   setAppliedPromo(validCodes[promoCode.toUpperCase()]);
    //   showNotification(`Promo code "${promoCode}" applied successfully! 🎉`);
    // } else {
    //   showNotification("Invalid promo code");
    // }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode("");
    showNotification("Promo code removed");
  };

  const calculateSubtotal = () => {
    return cartItems?.reduce((sum, item) => sum + item.book.price * 1, 0);
  };

  const calculateDiscount = () => {
    // const subtotal = calculateSubtotal();
    // if (!appliedPromo) return 0;

    // if (appliedPromo.type === "percentage") {
    //   return (subtotal * appliedPromo.discount) / 100;
    // }
    return 0;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const shipping = subtotal > 500 ? 0 : 50;
    return subtotal - discount + shipping;
  };

  const handleCheckout = () => {
    if (cartItems?.length === 0) {
      showNotification("Your cart is empty");
      return;
    }
    showNotification("Redirecting to secure checkout...");
    navigate(ROUTES.BOOKS_SELLING_CHECKOUT_PAGE);
  };

  const getGradeDisplay = (gradeLevels) => {
    if (!gradeLevels || gradeLevels?.length === 0) return "N/A";
    if (gradeLevels?.length === 1) return `Grade ${gradeLevels[0].name}`;
    return `Grades ${gradeLevels[0].name}–${
      gradeLevels[gradeLevels?.length - 1].name
    }`;
  };

  const subtotal = calculateSubtotal();
  const discount = calculateDiscount();
  const shipping = subtotal > 500 ? 0 : 50;
  const total = calculateTotal();

  return (
    <>
      <BaseNavbar activeItem="CART" />

      <div className="cart-container">
        {/* Header */}
        <section className="cart-header cart-fade-in">
          <div className="cart-header-content">
            <h1>🛒 Shopping Cart</h1>
            <p className="cart-header-subtitle">
              {cartItems?.length} {cartItems?.length === 1 ? "item" : "items"} in
              your cart
            </p>
          </div>
        </section>

        {loading ? (
          <section className="cart-main-content">
            <div className="cart-items-section">
              {[...Array(3)]?.map((_, i) => (
                <div
                  key={i}
                  className="cart-item-card"
                  style={{ padding: "1.5rem" }}
                >
                  <div style={{ display: "flex", gap: "1.5rem" }}>
                    <SkeletonLoading
                      customStyle={{
                        width: "120px",
                        height: "160px",
                        borderRadius: "10px",
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <SkeletonLoading
                        customStyle={{
                          width: "70%",
                          height: "24px",
                          marginBottom: "10px",
                        }}
                      />
                      <SkeletonLoading
                        customStyle={{
                          width: "50%",
                          height: "18px",
                          marginBottom: "15px",
                        }}
                      />
                      <SkeletonLoading
                        customStyle={{
                          width: "30%",
                          height: "20px",
                          marginBottom: "20px",
                        }}
                      />
                      <div style={{ display: "flex", gap: "20px" }}>
                        <SkeletonLoading
                          customStyle={{
                            width: "100px",
                            height: "40px",
                            borderRadius: "20px",
                          }}
                        />
                        <SkeletonLoading
                          customStyle={{
                            width: "80px",
                            height: "40px",
                            borderRadius: "20px",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-summary-section">
              <SkeletonLoading
                customStyle={{
                  width: "100%",
                  height: "400px",
                  borderRadius: "20px",
                }}
              />
            </div>
          </section>
        ) : cartItems?.length === 0 ? (
          <section className="cart-empty cart-fade-in">
            <div className="cart-empty-content">
              <div className="cart-empty-icon">🛒</div>
              <h2>Your Cart is Empty</h2>
              <p>Looks like you haven't added any books yet.</p>
              <button
                className="cart-btn cart-btn-primary"
                onClick={() => navigate(ROUTES.BOOKS_SELLING_HOME_PAGE)}
              >
                Browse Books 📚
              </button>
            </div>
          </section>
        ) : (
          <section className="cart-main-content cart-fade-in">
            {/* Cart Items */}
            <div className="cart-items-section">
              {cartItems?.map((item) => (
                <div key={item.book.id} className="cart-item-card">
                  <div className="cart-item-image">
                    <img src={item.book.cover_image_url} alt={item.book.title} />
                    {item.book.has_discount && (
                      <span className="cart-item-badge">
                        -{item.book.discount_percentage}%
                      </span>
                    )}
                  </div>

                  <div className="cart-item-details">
                    <div className="cart-item-header">
                      <div>
                        <h3 className="cart-item-title">{item.book.title}</h3>
                        <p className="cart-item-subtitle">{item.book.subtitle}</p>
                        <div className="cart-item-meta">
                          <span className="cart-meta-tag">{item.book.subject_display}</span>
                          <span className="cart-meta-tag">
                            {getGradeDisplay(item.book.grade_level)}
                          </span>
                        </div>
                      </div>
                      <button
                        className="cart-item-remove"
                        onClick={() => removeItem(item.book.id, item.book.title)}
                        disabled={processingItems.has(item.book.id)}
                      >
                        ✕
                      </button>
                    </div>

                    <div className="cart-item-footer">
                      <div className="cart-item-price">
                        <span className="current-price">₹{item.book.price}</span>
                        {item.book.has_discount && (
                          <span className="original-price">
                            ₹{item.book.original_price}
                          </span>
                        )}
                      </div>

                      {/* <div className="cart-quantity-controls">
                        <button
                          className="qty-btn"
                          onClick={() =>
                            updateQuantity(item.book.id, item.book.quantity - 1)
                          }
                          disabled={
                            item.book.quantity <= 1 || processingItems.has(item.book.id)
                          }
                        >
                          −
                        </button>
                        <span className="qty-value">{item.book.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() =>
                            updateQuantity(item.book.id, item.book.quantity + 1)
                          }
                          disabled={processingItems.has(item.book.id)}
                        >
                          +
                        </button>
                      </div> */}

                      <div className="cart-item-total">
                        <span className="total-label">Total:</span>
                        <span className="total-value">
                          ₹{item.book.price * 1}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="cart-summary-section">
              <div className="cart-summary-card">
                <h2 className="summary-title">Order Summary</h2>

                {/* Promo Code */}
                <div className="promo-section">
                  <label className="promo-label">Have a promo code?</label>
                  <div className="promo-input-group">
                    <input
                      type="text"
                      className="promo-input"
                      placeholder="Enter code"
                      value={promoCode}
                      onChange={(e) =>
                        setPromoCode(e.target.value.toUpperCase())
                      }
                      disabled={appliedPromo}
                    />
                    {appliedPromo ? (
                      <button
                        className="promo-btn promo-btn-remove"
                        onClick={removePromoCode}
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        className="promo-btn promo-btn-apply"
                        onClick={applyPromoCode}
                      >
                        Apply
                      </button>
                    )}
                  </div>
                  {appliedPromo && (
                    <div className="promo-success">
                      ✓ Promo code applied:{" "}
                      {appliedPromo.type === "percentage"
                        ? `${appliedPromo.discount}% off`
                        : `₹${appliedPromo.discount} off`}
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="summary-breakdown">
                  <div className="summary-row">
                    <span>Subtotal ({cartItems?.length} items)</span>
                    <span>₹{subtotal}</span>
                  </div>

                  {discount > 0 && (
                    <div className="summary-row discount-row">
                      <span>Discount</span>
                      <span className="discount-value">
                        −₹{discount.toFixed(0)}
                      </span>
                    </div>
                  )}

                  <div className="summary-row">
                    <span>Shipping</span>
                    <span className={shipping === 0 ? "free-shipping" : ""}>
                      {shipping === 0 ? "FREE" : `₹${shipping}`}
                    </span>
                  </div>

                  {subtotal <= 500 && shipping > 0 && (
                    <div className="shipping-notice">
                      Add ₹{(500 - subtotal).toFixed(0)} more for FREE shipping!
                      🚚
                    </div>
                  )}

                  <div className="summary-divider"></div>

                  <div className="summary-row summary-total">
                    <span>Total Amount</span>
                    <span className="total-amount">₹{total.toFixed(0)}</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <button
                  className="cart-btn cart-btn-checkout"
                  onClick={handleCheckout}
                >
                  Proceed to Checkout 🔒
                </button>

                {/* Trust Indicators */}
                <div className="trust-indicators">
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>Secure Payment</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>Free Returns</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>

              {/* Continue Shopping */}
              <button
                className="cart-btn cart-btn-secondary"
                onClick={() => navigate(ROUTES.BOOKS_SELLING_HOME_PAGE)}
              >
                ← Continue Shopping
              </button>
            </div>
          </section>
        )}

        {/* Notification */}
        {notification.visible && (
          <div className="cart-notification">{notification.message}</div>
        )}
      </div>

      <Footer />
    </>
  );
};

export default BookCartPage;
